import {Injectable} from '@angular/core';
import {CardWebElement} from '../../shared/card-web-element';
import {CardStoreService} from '../../shared/card-store.service';
import {CardDeck, DeckType, LinkageDirection, LinkageType, PathType} from '../../shared/card-deck';
import {CardWeb} from '../../shared/card-web';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {

  private linkageCardDeck: CardDeck;

  constructor(
      private css: CardStoreService
  ) {
    this.linkageCardDeck = css.getLinkageCardDeck();
  }

  getLinkageCardWebElementsForShortestPath(
      shortestPath: {posX: number, posY: number, distanceValue: number}[],
      sourceCard: CardWebElement,
      targetCard: CardWebElement)
  {
     // Ensure that the positions in shortestPath array are sorted from top to bottom
    shortestPath.sort(this.sortFunction);
    const linkageCardWebElements: CardWebElement[] = [];
    for (let i = 0; i < shortestPath.length - 2; i++) {
      const fromLocation = shortestPath[i];
      const cardLocation = shortestPath[i + 1];
      const toLocation = shortestPath[i + 2];
      const linkageCard = this.fetchLinkageCardFromCardDeck(fromLocation, cardLocation, toLocation, sourceCard, targetCard);
      linkageCardWebElements.push(linkageCard);
    }
    return linkageCardWebElements;
  }

  private deleteFromArray(array: number[], id: number) {
    const index = array.indexOf(id, 0);
    if (index > -1) {
      array.splice(index, 1);
    }
    return array;
  }

  reduceLinkageCard(linkageCardToReduce: CardWebElement, neighbourCard: CardWebElement) {
    let deleteUp = undefined;
    if (neighbourCard.posX + 1 === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY + 1) {
      linkageCardToReduce.linkageDownRightDirection = false;
      deleteUp = false;
    }else if (neighbourCard.posX - 1 === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY + 1) {
      linkageCardToReduce.linkageDownLeftDirection = false;
      deleteUp = false;
    }else if (neighbourCard.posX === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY + 2) {
      linkageCardToReduce.linkageDownMiddleDirection = false;
      deleteUp = false;
    }else if (neighbourCard.posX + 1 === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY - 1) {
      linkageCardToReduce.linkageUpRightDirection = false;
      deleteUp = true;
    }else if (neighbourCard.posX - 1 === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY - 1) {
      linkageCardToReduce.linkageUpLeftDirection = false;
      deleteUp = true;
    }else if (neighbourCard.posX === linkageCardToReduce.posX && neighbourCard.posY === linkageCardToReduce.posY - 2) {
      linkageCardToReduce.linkageUpMiddleDirection = false;
      deleteUp = true;
    }
    if (deleteUp !== undefined) {
      if (deleteUp) {
        linkageCardToReduce.linkageSourceIds = this.deleteFromArray(linkageCardToReduce.linkageSourceIds, neighbourCard.id);
      }else {
        linkageCardToReduce.linkageTargetIds = this.deleteFromArray(linkageCardToReduce.linkageTargetIds, neighbourCard.id);
      }
      linkageCardToReduce = this.updateLinkageType(linkageCardToReduce);
    }
    return linkageCardToReduce;
  }


  private fetchLinkageCardFromCardDeck(
      fromLocation: { posX: number; posY: number; distanceValue: number },
      cardLocation: { posX: number; posY: number; distanceValue: number },
      toLocation: { posX: number; posY: number; distanceValue: number },
      sourceCard: CardWebElement,
      targetCard: CardWebElement
  ): CardWebElement
  {
    const upDirection: LinkageDirection = this.determineLinkageCardDirection(fromLocation, cardLocation, 'up');
    const downDirection: LinkageDirection = this.determineLinkageCardDirection(cardLocation, toLocation, 'down');
    const cardName: string = upDirection + '_' + downDirection;
    const cardPattern =  this.linkageCardDeck.cardPatterns.find(card  => card.name === cardName);
    const sourceIds: number[] = [sourceCard.id];
    const targetIds: number[] = [targetCard.id];
    return {
      id: this.getUniqueIdForLocation(cardLocation),
      name: cardPattern.name,
      linkageType: LinkageType.oneToOne,
      linkageUpLeftDirection: upDirection === LinkageDirection.left,
      linkageUpRightDirection: upDirection === LinkageDirection.right,
      linkageUpMiddleDirection: upDirection === LinkageDirection.middle,
      linkageDownLeftDirection: downDirection === LinkageDirection.left,
      linkageDownRightDirection: downDirection === LinkageDirection.right,
      linkageDownMiddleDirection: downDirection === LinkageDirection.middle,
      linkageSourceIds: sourceIds,
      linkageTargetIds: targetIds,
      cardDeckId: cardPattern.cardDeckId,
      cardDeckType: cardPattern.cardDeckType,
      posX: cardLocation.posX,
      posY: cardLocation.posY
    };
  }

  private getUniqueIdForLocation(location: { posX: number; posY: number; distanceValue: number }) {
   return 1000000 + location.posX + 50 * location.posY;
  }

  private determineLinkageCardDirection(
      fromLocation: {posX: number, posY: number, distanceValue: number},
      toLocation: {posX: number, posY: number, distanceValue: number}, partOfCard: 'up' | 'down') {
    if (fromLocation.posX === toLocation.posX){
      if (fromLocation.posY + 2 === toLocation.posY) {
        return LinkageDirection.middle;
      }
    }

    if (fromLocation.posX - 1 === toLocation.posX){
      if (fromLocation.posY + 1 === toLocation.posY) {
        switch (partOfCard) {
          case 'up': {
            return LinkageDirection.right;
          }
          case 'down': {
            return LinkageDirection.left;
          }
          default: {}
        }
      }
    }

    if (fromLocation.posX + 1 === toLocation.posX){
      if (fromLocation.posY + 1 === toLocation.posY) {
        switch (partOfCard) {
          case 'up': {
            return LinkageDirection.left;
          }
          case 'down': {
            return LinkageDirection.right;
          }
          default: {}
        }
      }
    }
  }

  /*
   * Sort function to sort the shortestPath array by distanceValue from top to bottom
   */
  private sortFunction(a: {posX: number, posY: number, distanceValue: number}, b: {posX: number, posY: number, distanceValue: number}) {
    if (a.distanceValue === b.distanceValue) {
      return 0;
    }
    else {
      return (a.distanceValue < b.distanceValue) ? -1 : +1;
    }
  }

  searchShortestPath(
    sourceCard: CardWebElement,
    targetCard: CardWebElement,
    locationGrid: boolean[][],
    cardWeb: CardWeb,
    draggedCard: CardWebElement
  ) {
    // 1 Determine which card is higherBaseCard (positioned above) or lowerBaseCard (positioned below)
    let higherBaseCard: CardWebElement = sourceCard;
    let lowerBaseCard: CardWebElement = targetCard;
    if (sourceCard.posY > targetCard.posY) {
      // targetCard is positioned higher than target Card
      higherBaseCard = targetCard;
      lowerBaseCard = sourceCard;
    }else if (sourceCard.posY === targetCard.posY) {
      // sourceCard and targetCard are positioned on same level - not within reach
      return [];
    }

    // Check if there is a violation of relation direction
    if ((sourceCard.id === draggedCard.id) && (draggedCard.id === lowerBaseCard.id)) {
      return [];
    }else if ((targetCard.id === draggedCard.id) && (draggedCard.id === higherBaseCard.id)) {
      return [];
    }

    // 2 Fast check if lowerBaseCard is positioned out of reach + Determine maximum search spans
    // xSearchSpanLeft is the number of positions a possible path can be extended to the left at maximum
    let xSearchSpanLeft = 0;
    // xSearchSpanRight is the number of positions a possible path can be extended to the right at maximum
    let xSearchSpanRight = 0;
    // yDistanceBtwLowerAndHigherCard is the number of grid positions the lowerBaseCard is below the higherBaseCard
    // if the lowerBaseCard was shifted upwards left or right until lowerBaseCard.posX = higherBaseCard.posX
    let yDistanceBtwLowerAndHigherCard = 0;
    if (lowerBaseCard.posX <= higherBaseCard.posX) {
      // Case A: lowerBaseCard is positioned left from higherBaseCard
      // lowerBaseCard is unreachable if the sum of X and Y is smaller than higherBaseCard positions
      if ((lowerBaseCard.posX + lowerBaseCard.posY) < (higherBaseCard.posX + higherBaseCard.posY)) {
        return [];
      }
      yDistanceBtwLowerAndHigherCard = (lowerBaseCard.posY - (higherBaseCard.posX - lowerBaseCard.posX)) - higherBaseCard.posY;
      xSearchSpanLeft = lowerBaseCard.posX - (yDistanceBtwLowerAndHigherCard / 2);
      xSearchSpanRight = higherBaseCard.posX + (yDistanceBtwLowerAndHigherCard / 2);
    } else {
      // Case B: lowerBaseCard is positioned right from higherBaseCard
      // lowerBaseCard is unreachable if the difference between X and Y is greater than higherBaseCard positions
      if ((lowerBaseCard.posX - lowerBaseCard.posY) > (higherBaseCard.posX - higherBaseCard.posY)) {
        return [];
      }
      yDistanceBtwLowerAndHigherCard = (lowerBaseCard.posY - (lowerBaseCard.posX - higherBaseCard.posX)) - higherBaseCard.posY;
      xSearchSpanRight = lowerBaseCard.posX + (yDistanceBtwLowerAndHigherCard / 2);
      xSearchSpanLeft = higherBaseCard.posX - (yDistanceBtwLowerAndHigherCard / 2);
    }

    // 3 Create Set of all not grid locations between baseCard and sourceCard where the path can be possibly found
    // Initialize distanceValue array: all locations get an initial high and unreachable distance value
    let distanceValues = [];
    const rows = xSearchSpanRight + 1;
    const columns = lowerBaseCard.posY;
    for (let i = 0; i < rows; i++) {
      distanceValues[i] = [];
      for (let j = 0; j <= columns; j++) {
        distanceValues[i][j] = 10000;
      }
    }
    // Populate initial distanceValues with distance values
    for (let y = higherBaseCard.posY + 1; y <= lowerBaseCard.posY; y++) {
      // Fill distanceValues left from higherBaseCard
      for (let x = higherBaseCard.posX; x >= xSearchSpanLeft; x--) {
        distanceValues[x][y] = 10000; // Max distance
      }
      // Fill distanceValues right from higherBaseCard
      for (let x = higherBaseCard.posX + 1; x <= xSearchSpanRight; x++) {
        distanceValues[x][y] = 10000; // Max distance
      }
    }
    // Initialize location of higherBaseCard as start node with distance zero
    distanceValues[higherBaseCard.posX][higherBaseCard.posY] = 0;

    // 4 Walk through distanceValues recursively and adjust distance values of grid positions
    const visitedPositions: {posX: number, posY: number, distanceValue: number}[] = [];
    const stopLocation = {posX: lowerBaseCard.posX, posY: lowerBaseCard.posY};
    const startLocation = {posX: xSearchSpanLeft, posY: higherBaseCard.posY};
    const endLocation = {posX: xSearchSpanRight, posY: lowerBaseCard.posY};
    distanceValues = this.recursiveCalculationOfDistanceValues(
        stopLocation,
        distanceValues,
        startLocation,
        endLocation,
        visitedPositions,
        locationGrid,
        higherBaseCard.id,
        lowerBaseCard.id,
        cardWeb,
        false
    );
    // 5 Recreate shortest path from the distance value map from bottom to top
    if (distanceValues){
      return this.recreateShortestPath(lowerBaseCard, higherBaseCard, distanceValues);
    }else{
      return [];
    }
  }

  private recursiveCalculationOfDistanceValues(
      stopLocation: {posX, posY},
      distanceValues: number[][],
      startLocation: {posX, posY},
      endLocation: {posX, posY},
      visitedPositions: {posX, posY, distanceValue}[],
      locationGrid: boolean[][],
      higherBaseCardId: number,
      lowerBaseCardId: number,
      cardWeb: CardWeb,
      isOnLinkagePath: boolean
  ) {
    // reduced addedDistanceValue for usable linkageCards
    const linkAddValue = 0.5;
    const location = this.fetchGridPositionWithLowestValue(
        distanceValues, startLocation, endLocation, visitedPositions);
    visitedPositions.push(location);
    if (location.posX !== null) {
      const allLinkageCards = cardWeb.cards.filter(card => card.cardDeckType === DeckType.linkage);
      const localLinkageElement = allLinkageCards.find(card => card.posX === location.posX && card.posY === location.posY);

      // 1: Check if  down left location is not already occupied by a card
      if (!locationGrid[location.posX - 1][location.posY + 1]) {
        // Calculate distance value for lower left location
        if (((location.posX - 1) >= startLocation.posX) && (location.posY + 1) <= endLocation.posY) {
          if (distanceValues[location.posX - 1][location.posY + 1] > location.distanceValue + 1) {
            if (!isOnLinkagePath) {
              distanceValues[location.posX - 1][location.posY + 1] = location.distanceValue + 1;
            }
          }
        }
      }else {
        // Check if there is a usable linkageCard
        const cardWebElement = cardWeb.cards.find(card => card.posX === location.posX - 1 && card.posY === location.posY + 1);
        if (cardWebElement.cardDeckType === DeckType.linkage) {
          if (localLinkageElement === undefined ||
            this.isCompatibleLinkageType(localLinkageElement, cardWebElement, LinkageDirection.left)) {
            // Does linkage card lead to lowerBaseCard or originates from higherBaseCard?
            if (cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)) {
              // Attention: If it is already a oneToMany linkageCard no additional incoming link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.oneToMany ||
                cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
                if (distanceValues[location.posX - 1][location.posY + 1] > location.distanceValue + 1) {
                  // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                  distanceValues[location.posX - 1][location.posY + 1] = location.distanceValue + linkAddValue;
                  // isOnLinkagePath = true;
                }
              }
            }else if (cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
              // Attention: If it is already a manyToOne linkageCard no additional outgoing link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.manyToOne ||
                cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)) {
                if (distanceValues[location.posX - 1][location.posY + 1] > location.distanceValue + 1) {
                  // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                  distanceValues[location.posX - 1][location.posY + 1] = location.distanceValue + linkAddValue;
                  // isOnLinkagePath = true;
                }
              }
            }
          }
        }else{
          // Check if card on lower left location is identical with stop location (= searched baseCard)
          if ((stopLocation.posX === location.posX - 1) && (stopLocation.posY === location.posY + 1)) {
            distanceValues[location.posX - 1][location.posY + 1] = location.distanceValue + 1;
            return distanceValues;
          }
        }
      }

      // 2: Check if down right location is not already occupied by a card
      if (!locationGrid[location.posX + 1][location.posY + 1]) {
        if (((location.posX + 1) <= endLocation.posX) && (location.posY + 1) <= endLocation.posY) {
          // Calculate distance value for lower right location
          if (distanceValues[location.posX + 1][location.posY + 1] > location.distanceValue + 1) {
            if (!isOnLinkagePath) {
              distanceValues[location.posX + 1][location.posY + 1] = location.distanceValue + 1;
            }
          }
        }
      }else {
        // Check if there is a usable linkageCard
        const cardWebElement = cardWeb.cards.find(card => card.posX === location.posX + 1 && card.posY === location.posY + 1);
        if (cardWebElement.cardDeckType === DeckType.linkage) {
          if (localLinkageElement === undefined ||
            this.isCompatibleLinkageType(localLinkageElement, cardWebElement, LinkageDirection.right)) {
            // Does linkage card lead to lowerBaseCard or originates from higherBaseCard?
            if (cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)){
              // Attention: If it is already a oneToMany linkageCard no additional incoming link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.oneToMany ||
                cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
                // Ensure that there is no indexOutOfBounds Exception for the array of distanceValues
                if (distanceValues.length > location.posX + 1 && distanceValues[location.posX + 1].length > location.posY + 1) {
                  if (distanceValues[location.posX + 1][location.posY + 1] > location.distanceValue + 1) {
                    // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                    distanceValues[location.posX + 1][location.posY + 1] = location.distanceValue + linkAddValue;
                    // isOnLinkagePath = true;
                  }
                }
              }
            }else if (cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
              // Attention: If it is already a manyToOne linkageCard no additional outgoing link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.manyToOne ||
                cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)) {
                // Ensure that there is no indexOutOfBounds Exception for the array of distanceValues
                if (distanceValues.length > location.posX + 1 && distanceValues[location.posX + 1].length > location.posY + 1) {
                  if (distanceValues[location.posX + 1][location.posY + 1] > location.distanceValue + 1) {
                    // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                    distanceValues[location.posX + 1][location.posY + 1] = location.distanceValue + linkAddValue;
                    // isOnLinkagePath = true;
                  }
                }
              }
            }
          }
        }else {
          // Check if card on lower right location is identical with stop location (= searched baseCard)
          if ((stopLocation.posX === location.posX + 1) && (stopLocation.posY === location.posY + 1)) {
            distanceValues[location.posX + 1][location.posY + 1] = location.distanceValue + 1;
            return distanceValues;
          }
        }
      }

      // 3: Check if down middle location is not already occupied by a card
      if (!locationGrid[location.posX][location.posY + 2]) {
        if (((location.posX) <= endLocation.posX) && (location.posY + 2) <= endLocation.posY) {
          // Calculate distance value for lower top location
          if (distanceValues[location.posX][location.posY + 2] > location.distanceValue + 1) {
            if (!isOnLinkagePath) {
              distanceValues[location.posX][location.posY + 2] = location.distanceValue + 1;
            }
          }
        }
      }else {
        // Check if there is a usable linkageCard
        const cardWebElement = cardWeb.cards.find(card => card.posX === location.posX && card.posY === location.posY + 2);
        if (cardWebElement.cardDeckType === DeckType.linkage) {
          if (localLinkageElement === undefined ||
            this.isCompatibleLinkageType(localLinkageElement, cardWebElement, LinkageDirection.middle)) {
            // Does linkage card lead to lowerBaseCard or originates from higherBaseCard?
            if (cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)){
              // Attention: If it is already a oneToMany linkageCard no additional incoming link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.oneToMany ||
                cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
                if (distanceValues[location.posX][location.posY + 2] > location.distanceValue + 1) {
                  // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                  distanceValues[location.posX][location.posY + 2] = location.distanceValue + linkAddValue;
                  // isOnLinkagePath = true;
                }
              }
            }else if (cardWebElement.linkageSourceIds.includes(+higherBaseCardId)) {
              // Attention: If it is already a manyToOne linkageCard no additional outgoing link allowed
              const overallLinkageType = this.getOverallLinkageType(cardWebElement, allLinkageCards);
              if (overallLinkageType !== LinkageType.manyToOne ||
                cardWebElement.linkageTargetIds.includes(+lowerBaseCardId)) {
                if (distanceValues[location.posX][location.posY + 2] > location.distanceValue + 1) {
                  // Add only 0.5 to favour linkageRoutes in the recreationOfShortestPath-Method
                  distanceValues[location.posX][location.posY + 2] = location.distanceValue + linkAddValue;
                  // isOnLinkagePath = true;
                }
              }
            }
          }
        }else{
          // Check if card on lower top location is identical with stop location (= searched baseCard)
          if ((stopLocation.posX === location.posX) && (stopLocation.posY === location.posY + 2)) {
            distanceValues[location.posX][location.posY + 2] = location.distanceValue + 1;
            return distanceValues;
          }
        }
      }

      return this.recursiveCalculationOfDistanceValues(
          stopLocation,
          distanceValues,
          startLocation,
          endLocation,
          visitedPositions,
          locationGrid,
          higherBaseCardId,
          lowerBaseCardId,
          cardWeb,
          isOnLinkagePath
      );
    }else{
      return distanceValues;
    }
  }

  private recreateShortestPath(lowerBaseCard: CardWebElement, higherBaseCard: CardWebElement, distanceValues: number[][]){
    let searchingForBaseCard = true;
    const location: {posX: number, posY: number} = {posX: lowerBaseCard.posX, posY: lowerBaseCard.posY};
    const shortestPath: {posX: number, posY: number, distanceValue: number} [] = [];

    // If a path was found, the distanceValue of lowerBaseCard must be a low integer, not 10000 (=defaultValue)
    if (distanceValues[lowerBaseCard.posX][lowerBaseCard.posY] === 10000) {
      return [];
    }
    // Add lowerBaseCard as startingPoint of shortest path
    shortestPath.push({
      posX: lowerBaseCard.posX,
      posY: lowerBaseCard.posY,
      distanceValue: distanceValues[lowerBaseCard.posX][lowerBaseCard.posY]}
    );

    // Iteratively walk through distanceValues from lowerBaseCard to higherBaseCard to look for shortest path
    do {
      if (higherBaseCard.posX === location.posX && higherBaseCard.posY === location.posY) {
        searchingForBaseCard = false;
      }else{
        const topLeftValue = distanceValues[location.posX - 1][location.posY - 1];
        const topValue = distanceValues[location.posX][location.posY  - 2];
        let topRightValue = 10000;
        if (location.posX + 1 < distanceValues.length ) {
          topRightValue = distanceValues[location.posX + 1][location.posY - 1];
        }
        let goTopLeft = false;
        let goTopMiddle = false;
        let goTopRight = false;
        goTopLeft = topLeftValue <= Math.min(topValue, topRightValue);
        goTopMiddle = topValue <= Math.min(topLeftValue, topRightValue);
        goTopRight = topRightValue <= Math.min(topLeftValue, topValue);

        // Ensure that upwardDirection is preferred if it has equal distanceValue with right or left

        /*if (goTopLeft && goTopMiddle) {
          if (higherBaseCard.posX < location.posX) {
            goTopMiddle = false;
          }
        }
        if (goTopRight && goTopMiddle) {
          if (higherBaseCard.posX > location.posX) {
            goTopMiddle = false;
          }
        }*/

        if (goTopLeft) {
          shortestPath.push({posX: location.posX - 1, posY: location.posY  - 1, distanceValue: topLeftValue});
          location.posX = location.posX - 1;
          location.posY  = location.posY  - 1;
        }else if (goTopMiddle){
          shortestPath.push({posX: location.posX, posY: location.posY  - 2, distanceValue: topValue});
          location.posY  = location.posY  - 2;
        }else if (goTopRight) {
          shortestPath.push({posX: location.posX + 1, posY: location.posY  - 1, distanceValue: topRightValue});
          location.posX = location.posX + 1;
          location.posY  = location.posY  - 1;
        }
      }
    } while (searchingForBaseCard);
    return shortestPath;
  }

  private fetchGridPositionWithLowestValue(
      distanceValues: number[][], startLocation: {posX: number, posY: number}, endLocation: {posX: number, posY: number},
      searchedPositions: {posX: number, posY: number, distanceValue: number}[]) {
    let posX = null;
    let posY = null;
    let distanceValue = 10000;
    for (let y = startLocation.posY; y <= endLocation.posY; y++) {
      for (let x = startLocation.posX; x <= endLocation.posX; x++) {
        if (distanceValues[x][y] < distanceValue) {
          if (!this.checkIfPositionWasAlreadySearched(x, y, searchedPositions)) {
            posX = x;
            posY = y;
            distanceValue = distanceValues[x][y];
          }
        }
      }
    }
    return {posX, posY, distanceValue};
  }

  private checkIfPositionWasAlreadySearched(
      posX: number, posY: number,  searchedPositions: {posX: number, posY: number, distanceValue: number}[]) {
    for (let i = 0; i < searchedPositions.length; i++) {
      if (searchedPositions[i]){
        if (searchedPositions[i].posY === posY && searchedPositions[i].posX === posX) {
          return true;
        }
      }
    }
    return false;
  }

  private mergeLinkageSourceAndTarget(actualLinkageCard: CardWebElement, newLinkageCard: CardWebElement) {
    for (const sourceId of newLinkageCard.linkageSourceIds) {
      if (!actualLinkageCard.linkageSourceIds.includes(sourceId)) {
        actualLinkageCard.linkageSourceIds.push(sourceId);
      }
    }
    for (const targetId of newLinkageCard.linkageTargetIds) {
      if (!actualLinkageCard.linkageTargetIds.includes(targetId)) {
        actualLinkageCard.linkageTargetIds.push(targetId);
      }
    }
    actualLinkageCard = this.updateLinkageType(actualLinkageCard);
    return actualLinkageCard;
  }

  private updateLinkageType(actualLinkageCard: CardWebElement) {
    const upDirections = actualLinkageCard.linkageSourceIds.length;
    const downDirections = actualLinkageCard.linkageTargetIds.length;
    if (upDirections < downDirections) {
      actualLinkageCard.linkageType = LinkageType.oneToMany;
    }else if (downDirections < upDirections) {
      actualLinkageCard.linkageType = LinkageType.manyToOne;
    }else if (downDirections === upDirections && downDirections === 1) {
      actualLinkageCard.linkageType = LinkageType.oneToOne;
    }else{
      actualLinkageCard.linkageType = LinkageType.manyToMany;
      console.error('Merge of linkage cards to create manyToMany LinkageType not allowed');
    }
    return actualLinkageCard;
  }
  mergeLinkageCards(actualLinkageCard: CardWebElement, newLinkageCard: CardWebElement) {
    actualLinkageCard = this.mergeLinkageSourceAndTarget(actualLinkageCard, newLinkageCard);
    actualLinkageCard.linkageUpLeftDirection ||= newLinkageCard.linkageUpLeftDirection;
    actualLinkageCard.linkageUpMiddleDirection ||= newLinkageCard.linkageUpMiddleDirection;
    actualLinkageCard.linkageUpRightDirection ||= newLinkageCard.linkageUpRightDirection;
    actualLinkageCard.linkageDownLeftDirection ||= newLinkageCard.linkageDownLeftDirection;
    actualLinkageCard.linkageDownRightDirection ||= newLinkageCard.linkageDownRightDirection;
    actualLinkageCard.linkageDownMiddleDirection ||= newLinkageCard.linkageDownMiddleDirection;
    const upArray = [
      actualLinkageCard.linkageUpLeftDirection,
      actualLinkageCard.linkageUpRightDirection,
      actualLinkageCard.linkageUpMiddleDirection
    ];
    const downArray = [
      actualLinkageCard.linkageDownRightDirection,
      actualLinkageCard.linkageDownLeftDirection,
      actualLinkageCard.linkageDownMiddleDirection
    ];
    const upArrayCount = upArray.filter(Boolean).length;
    const downArrayCount = downArray.filter(Boolean).length;
    if (upArrayCount < downArrayCount) {
      if (actualLinkageCard.linkageType !== LinkageType.oneToMany) {
        console.error('Problem with LinkageDirection of Merged Card', actualLinkageCard);
      }
    }else if (downArrayCount < upArrayCount) {
      if (actualLinkageCard.linkageType !== LinkageType.manyToOne) {
        console.error('Problem with LinkageDirection of Merged Card', actualLinkageCard);
      }
    }
    return actualLinkageCard;
  }

  getPathTypeForLinkageCard(linkageCard: CardWebElement) {
    const upArray = [
      linkageCard.linkageUpLeftDirection,
      linkageCard.linkageUpRightDirection,
      linkageCard.linkageUpMiddleDirection
    ];
    const downArray = [
      linkageCard.linkageDownRightDirection,
      linkageCard.linkageDownLeftDirection,
      linkageCard.linkageDownMiddleDirection
    ];
    const upArrayCount = upArray.filter(Boolean).length;
    const downArrayCount = downArray.filter(Boolean).length;
    if (upArrayCount === downArrayCount && upArrayCount === 1) {
      return PathType.oneToOne;
    }
    if (upArrayCount > downArrayCount && downArrayCount === 1) {
      return PathType.manyToOne;
    }
    if (downArrayCount > upArrayCount && upArrayCount === 1) {
      return PathType.oneToMany;
    }
    if (downArrayCount > 1 && upArrayCount > 1) {
      console.error('PathType of manyToMany at LinkageCard ' + linkageCard.id);
      return PathType.manyToMany;
    }else{
      console.error('Unknown PathType at LinkageCard ' + linkageCard.id);
    }
  }

  private isCompatibleLinkageType(
    localLinkageElement: CardWebElement,
    nextLinkageElement: CardWebElement,
    nextInDirection: LinkageDirection)
  {
    let isCompatible = true;
    if (localLinkageElement.linkageType === LinkageType.oneToMany && nextLinkageElement.linkageType === LinkageType.manyToOne) {
      isCompatible = false;
    }
    if (localLinkageElement.linkageType === LinkageType.manyToOne && nextLinkageElement.linkageType === LinkageType.oneToMany) {
      isCompatible = false;
    }
    if (localLinkageElement.linkageType === LinkageType.oneToOne) {
      switch (nextInDirection) {
        case LinkageDirection.left: {
          isCompatible = (localLinkageElement.linkageDownLeftDirection && nextLinkageElement.linkageUpRightDirection);
          break;
        }
        case LinkageDirection.right: {
          isCompatible = (localLinkageElement.linkageDownRightDirection && nextLinkageElement.linkageUpLeftDirection);
          break;
        }
        case LinkageDirection.middle: {
          isCompatible = (localLinkageElement.linkageDownMiddleDirection && nextLinkageElement.linkageUpMiddleDirection);
          break;
        }
        default: {
          isCompatible = false;
        }
      }
    }
    return isCompatible;
  }

  getAllLinkageCardsForTargetId(cardId: number, allLinkageCards: CardWebElement[]) {
    return allLinkageCards.filter(
      card => card.linkageTargetIds.includes(cardId)
    );
  }

  getAllLinkageCardsForSourceId(cardId: number, allLinkageCards: CardWebElement[]) {
    return allLinkageCards.filter(
      card => card.linkageSourceIds.includes(cardId)
    );
  }

  getOverallLinkageType(linkageCard: CardWebElement, allLinkageCards: CardWebElement[]) {
    if (linkageCard.linkageType === LinkageType.oneToOne) {
      const allTargetCards = this.getAllLinkageCardsForTargetId(linkageCard.linkageTargetIds[0], allLinkageCards);
      const allSourceCards = this.getAllLinkageCardsForSourceId(linkageCard.linkageSourceIds[0], allLinkageCards);
      const allCardsOnPath = allTargetCards.filter(value => allSourceCards.includes(value));
      for (const cardOnPath of allCardsOnPath) {
        if (cardOnPath.linkageType !== LinkageType.oneToOne) {
          return cardOnPath.linkageType;
        }
      }
      return LinkageType.oneToOne;
    }else{
      return linkageCard.linkageType;
    }
  }
}
